import { useCategoriesPlugin } from "@/app-autocomplete/plugins/useCategoriesPlugin.tsx";
import { useContentPlugin } from "@/app-autocomplete/plugins/useContentPlugin.tsx";
import { useProductPlugin } from "@/app-autocomplete/plugins/useProductPlugin.tsx";
import { useStaticCategoriesPlugin } from "@/app-autocomplete/plugins/useStaticCategoriesPlugin.tsx";
import { useAlgoliaShopConfig } from "@/providers/AlgoliaShopConfigProvider.tsx";
import type { AlgoliaAutocompleteHit } from "@/types/AlgoliaAutocompleteHit.ts";
import { type AutocompleteState, createAutocomplete } from "@algolia/autocomplete-core";
import type { FacetFilters, Hit } from "algoliasearch";
import {
	type BaseSyntheticEvent,
	createContext,
	type Dispatch,
	type FC,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	type RefObject,
	type SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

type AutocompleteCoreState = AutocompleteState<AlgoliaAutocompleteHit>;
interface AutocompleteContextProps {
	autocompleteState: AutocompleteCoreState;
	setAutocompleteState: Dispatch<SetStateAction<AutocompleteCoreState>>;
	setFilters: Dispatch<SetStateAction<string>>;
	setFacetFilters: Dispatch<SetStateAction<FacetFilters>>;
	autocomplete: ReturnType<typeof createAutocomplete<Hit<AlgoliaAutocompleteHit>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>>;
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	inputRef: RefObject<HTMLInputElement | null>;
	formRef: RefObject<HTMLFormElement | null>;
	panelRef: RefObject<HTMLDivElement | null>;
	categoriesLayoutRef: RefObject<HTMLDivElement | null>;
	productsLayoutRef: RefObject<HTMLDivElement | null>;
	contentLayoutRef: RefObject<HTMLDivElement | null>;
	totalHits?: number;
	areaInViewState: string | undefined;
	setAreaInViewState: Dispatch<SetStateAction<string | undefined>>;
}

const AutocompleteContext = createContext<AutocompleteContextProps | undefined>(undefined);

export const AutocompleteProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const categoriesLayoutRef = useRef<HTMLDivElement>(null);
	const productsLayoutRef = useRef<HTMLDivElement>(null);
	const contentLayoutRef = useRef<HTMLDivElement>(null);
	const {
		tc,
		searchClient,
		algoliaConfig: { customizedUrl, categoryData, autocompleteSuggestionsHits, currentSearch, autocompleteContentHits, algoliaContentSearchIndexName, algoliaShopFilter },
	} = useAlgoliaShopConfig();

	const [filters, setFilters] = useState<string>(algoliaShopFilter ?? "");
	const [facetFilters, setFacetFilters] = useState<FacetFilters>("");
	const [totalHits] = useState<number>();
	const [areaInViewState, setAreaInViewState] = useState<string>();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const [autocompleteState, setAutocompleteState] = useState<AutocompleteCoreState>({
		query: "",
		collections: [],
		isOpen: false,
		activeItemId: null,
		completion: null,
		context: {},
		status: "idle",
	});

	const filtersRef = useRef(filters);
	const facetFiltersRef = useRef(facetFilters);
	const productPlugin = useProductPlugin(filtersRef, facetFiltersRef);
	const categoriesPlugin = useCategoriesPlugin();

	const autocomplete = useMemo(
		() =>
			createAutocomplete<AlgoliaAutocompleteHit, BaseSyntheticEvent, MouseEvent, KeyboardEvent>({
				onSubmit({ state }) {
					window.location.href = `/${customizedUrl}/search/?input_search=${state.query}`;
				},
				onStateChange({ state }) {
					setAutocompleteState(state);
				},
				initialState: {
					query: currentSearch ?? "",
				},
				insights: true,
				placeholder: tc.placeholder,
				openOnFocus: true,
				navigator: {
					navigate({ itemUrl }) {
						window.location.assign(itemUrl);
					},
					navigateNewTab({ itemUrl }) {
						const windowReference = window.open(itemUrl, "_blank", "noopener");

						if (windowReference) {
							windowReference.focus();
						}
					},
					navigateNewWindow({ itemUrl }) {
						window.open(itemUrl, "_blank", "noopener");
					},
				},
				// eslint-disable-next-line react-hooks/rules-of-hooks
				plugins: [
					categoriesPlugin,
					productPlugin,
					// eslint-disable-next-line react-hooks/rules-of-hooks
					useContentPlugin(searchClient, algoliaContentSearchIndexName, autocompleteContentHits),
					// eslint-disable-next-line react-hooks/rules-of-hooks
					useStaticCategoriesPlugin(categoryData, autocompleteSuggestionsHits),
				],
			}),
		[]
	);
	useEffect(() => {
		filtersRef.current = filters;
		facetFiltersRef.current = facetFilters;
		autocomplete.refresh().then();
	}, [filters, facetFilters, autocomplete]);

	useEffect(() => {
		if (!isModalOpen) return;
		if (!panelRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				// Get only visible sections
				const visibleSections = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);

				if (visibleSections.length === 0) return;

				const topSection = visibleSections[0].target;
				if (topSection === productsLayoutRef.current) {
					setAreaInViewState("products");
				} else if (topSection === contentLayoutRef.current) {
					setAreaInViewState("content");
				} else if (topSection === categoriesLayoutRef.current) {
					setAreaInViewState("categories");
				}
			},
			{
				root: panelRef.current, // 👈 VERY IMPORTANT (scroll container)
				threshold: [0.01], // prevents flickering
			}
		);

		const sections = [categoriesLayoutRef.current, productsLayoutRef.current, contentLayoutRef.current];

		sections.forEach((section) => {
			if (section) observer.observe(section);
		});

		return () => {
			observer.disconnect();
		};
	}, [isModalOpen]);

	const { getEnvironmentProps } = autocomplete;

	useEffect(() => {
		if (!formRef.current || !panelRef.current || !inputRef.current) {
			return undefined;
		}
		const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
			formElement: formRef.current,
			inputElement: inputRef.current,
			panelElement: panelRef.current,
		});
		window.addEventListener("mousedown", onMouseDown);
		window.addEventListener("touchstart", onTouchStart);
		window.addEventListener("touchmove", onTouchMove);
		return () => {
			window.removeEventListener("mousedown", onMouseDown);
			window.removeEventListener("touchstart", onTouchStart);
			window.removeEventListener("touchmove", onTouchMove);
		};
	}, [getEnvironmentProps, autocompleteState.isOpen]);
	return (
		<AutocompleteContext.Provider
			value={{
				panelRef,
				formRef,
				inputRef,
				autocompleteState,
				setAutocompleteState,
				autocomplete,
				setFilters,
				setFacetFilters,
				setIsModalOpen,
				isModalOpen,
				totalHits,
				contentLayoutRef,
				categoriesLayoutRef,
				productsLayoutRef,
				areaInViewState,
				setAreaInViewState,
			}}
		>
			{children}
		</AutocompleteContext.Provider>
	);
};

export const useAutocomplete = () => {
	const context = useContext(AutocompleteContext);
	if (!context) {
		throw new Error("useAutocomplete must be used within an AutocompleteProvider");
	}
	return context;
};
